<template>
	<div class="p-p-2 child-content">
		<SystemCsvTranslationImport
			:showErrorMessage="showErrorMessage"
			:selectedFiles="files"
			:fileTypes="fileTypes"
			@update="onUpdate"
			@fileUploader="fileUploader"
		/>
	</div>

	<EntryDialog
		:show="showWarningDialog"
		@onClose="showWarningDialog = false"
		@onConfirm="startFileUpload"
		:headerText="$t('Squeeze.System.CreateResetTranslations')"
		:saveButtonText="$t('Squeeze.General.Execute')"
	>
		<template #content>
			<b>{{ $t('Squeeze.General.Attention') }}</b>: {{ $t('Squeeze.System.CreateResetTranslationsConfirm') }}
		</template>
	</EntryDialog>

	<Dialog :header="$t('Squeeze.General.Info')" v-model:visible="showImportErrors" :modal="true">
		<Message
			:closable="false"
			severity="info"
		>
			{{ $t('Squeeze.General.SuccessButWithErrors') }}<br/>
			{{ $t('Squeeze.General.CountOfErrors', { errorNumber: errorCount }) }}
		</Message>
		<Message
			:closable="false"
			severity="error"
		>
			<template v-for="error in importErrors" v-bind:key="error">
				{{ error }}<br/>
			</template>
		</Message>
	</Dialog>
</template>

<script lang="ts">
import {defineComponent, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import SystemCsvTranslationImport from "@/apps/administration/components/system/SystemImport.vue";
import {UploadFile} from "@/shims-prime-vue";
import EntryDialog from "@/components/EntryDialog.vue";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";

interface TranslationImportSettings {
	csvSeparator: string;
	fileType: string;
	skipErrors: boolean;
}

interface TranslationImportResponse {
	errorLines?: string[];
	errorLineAmount?: number;
}

export default defineComponent({
	name: "TranslationImportView",
	components: {
		Dialog,
		EntryDialog,
		Message,
		SystemCsvTranslationImport,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();

		/** List of all files */
		const files = ref<UploadFile[]>([]);

		/** Used CSV-Separator */
		const csvSeparator = ref(';');

		/** Skip error lines? */
		const skipError = ref<boolean>(false);

		/** List with Available File-Types */
		const fileTypes = [
			{
				id: 'csv',
				description: t('Squeeze.System.CSV'),
			},
		];

		/** Used File-Type for Upload */
		const fileType = ref('csv');

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Should the reset dialog be shown? */
		const showWarningDialog = ref<boolean>(false);

		/** Should the Import Error Dialog be shown? */
		const showImportErrors = ref<boolean>(false);

		/** List of errors of import */
		const importErrors = ref<string[]>([]);

		/** Count of errors */
		const errorCount = ref<number>(0);

		/**
		 * Triggered on update of the translation CSV import-form
		 * @param data
		 * @param isInvalid
		 */
		const onUpdate = (data: TranslationImportSettings, isInvalid: boolean) => {
			showErrorMessage.value = isInvalid;
			csvSeparator.value = data.csvSeparator;
			skipError.value = data.skipErrors;
			fileType.value = data.fileType;
		}

		/**
		 * Manual file upload to the Squeeze API. This has been programmed because the generated API client does not
		 * support multipart/form-data requests: https://github.com/swagger-api/swagger-codegen/issues/3921
		 * @param file
		 * @param fileType
		 * @param csvSeparator
		 */
		const manualFileUpload = async (file: UploadFile, fileType: string, csvSeparator: string) => {
			const body = new FormData();
			body.set("file", file);

			const response = await ClientManager.getInstance().customFetch(ClientManager.getInstance().getSqueezeBasePath() +
				"/translations/import?format=" + fileType + "&csv-separator=" + encodeURIComponent(csvSeparator) + "&skipErrors=" + skipError.value, {
				method: "POST",
				body: body,
			});

			if (response.status !== 200 && response.status !== 204) {
				const responseJSON = await response.json() as { message: string };
				throw new Error("Unexpected status " + responseJSON.message);
			}

			if (response.status === 204) {
				return;
			}

			const responseJSON = await response.json() as TranslationImportResponse;
			const errorLines = responseJSON.errorLines || [];
			if (errorLines.length > 0) {
				importErrors.value = errorLines;
				errorCount.value = responseJSON.errorLineAmount || errorLines.length;
				showImportErrors.value = true;
			}
		}

		/**
		 * Starts the File upload for the given files
		 */
		const startFileUpload = () => {
			showWarningDialog.value = false;
			files.value
				.forEach((file: UploadFile, index: number) => {
					const idx = index;
					files.value[idx].error = false;
					files.value[idx].errorText = "";
					files.value[idx].loading = true;
					files.value = [...files.value];

					manualFileUpload(file, fileType.value, csvSeparator.value)
						.then(() => {
							files.value[idx].uploadFinished = true;
							ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.System.SuccessDataUpload'));
						})
						.catch(err => {
							files.value[idx].error = true;
							files.value[idx].errorText = err.message;
							ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
						})
						.finally(() => {
							files.value[idx].loading = false;
							files.value = [...files.value];
						});
				})
		}

		/** Uploads the files from the file-uploader
		 * @param filesSend
		 */
		const fileUploader = (filesSend: UploadFile[]) => {
			files.value = filesSend;
			showWarningDialog.value = true;
		}

		return {
			errorCount,
			fileTypes,
			files,
			importErrors,
			showErrorMessage,
			showImportErrors,
			showWarningDialog,
			fileUploader,
			onUpdate,
			startFileUpload,
		};
	},
});
</script>

<style scoped>

.child-content {
	height: calc(100vh - 5.6rem - 4.1rem);
}

</style>
